import { ChevronRightIcon } from "@heroicons/react/16/solid";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";

export type NavigationNode = {
    id: string;
    name: string;
    nodes?: NavigationNode[];
    anchor?: string;
};

export function NavigationNode({
    node,
    openIds,
    setIsOpen,
    level = 0,
    onNavigate,
    activeId,
}: {
    node: NavigationNode;
    openIds: string[];
    setIsOpen: (open: boolean) => void;
    level?: number;
    onNavigate?: (node: NavigationNode) => void;
    activeId?: string;
}) {
    const isOpen = openIds.includes(node.id);
    const hasChildren = node.nodes && node.nodes.length > 0;
    const isActive = activeId === node.id;

    const handleClick = () => {
        if (!hasChildren && onNavigate) {
            onNavigate(node);
        }
    };

    return (
        <li key={node.id}>
            <span
                className={clsx(
                    "hover:bg-hover flex cursor-pointer items-center gap-1.5 rounded px-2 py-1",
                    isActive && "bg-selected",
                    level === 0 && "py-2",
                )}
                onClick={handleClick}
            >
                <p
                    className={clsx(
                        "flex-1 cursor-pointer",
                        level === 0 ? "lozenge" : "body-s",
                    )}
                >
                    {node.name}
                </p>

                {hasChildren && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(!isOpen);
                        }}
                        className="no-styles -m-1 p-1"
                    >
                        <motion.span
                            animate={{ rotate: isOpen ? 90 : 0 }}
                            transition={{
                                type: "spring",
                                bounce: 0,
                                duration: 0.4,
                            }}
                            className="flex"
                        >
                            <ChevronRightIcon className="size-4 text-gray-500" />
                        </motion.span>
                    </button>
                )}
            </span>

            <AnimatePresence>
                {isOpen && hasChildren && (
                    <motion.ul
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{
                            type: "spring",
                            bounce: 0,
                            duration: 0.4,
                        }}
                        className={clsx(
                            "mt-2 flex flex-col justify-end gap-2 overflow-hidden pl-3",
                        )}
                    >
                        {node.nodes?.map((childNode) => (
                            <NavigationNode
                                node={childNode}
                                key={childNode.id}
                                openIds={openIds}
                                setIsOpen={setIsOpen}
                                level={level + 1}
                                onNavigate={onNavigate}
                                activeId={activeId}
                            />
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </li>
    );
}
